import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import FormDropdown, {
  TDropdownItem,
} from 'components/common/forms/FormDropdown';
import FormCheckbox from 'components/common/forms/FormCheckbox';
import LabelInput from 'components/common/forms/LabelInput';
import CustomDatePicker from 'components/common/display/CustomDatepicker';
import SingleBtn from 'components/common/elements/SingleBtn';
import SearchInputWithButton from 'components/common/forms/SearchInputWithButton';
import { ReactComponent as ResetIcon } from 'assets/icons/reset.svg';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import { ChartInfo } from 'constants/chart/common/chartInfo';
import { SearchFilterField, TSearchFilter } from 'types/form/searchFilter';
import { TAxiosResponse } from 'types/apis/api';
import {
  TGetCourseListResponse,
  TGetCourseInfoListResponse,
  TGetStudClassListResponse,
  TGetChartRoundResponse,
  TGetStudentUserListResponse,
} from 'types/apis/common';
import {
  getCourseList,
  getCourseInfoList,
  getStudentCourseListByProfessor,
  getStudClassList,
  getChartRoundList,
  getStudentUserList,
} from 'apis/common';
import { getChartRoundListByPatient } from 'apis/student';
import { format } from 'date-fns';

type TProps = {
  isCollective?: boolean;
  isInnerItem?: boolean;
  dropdown?: {
    label?: string;
    id: string;
    placeholder: string;
    width: string;
    useAll?: boolean;
  }[];
  input?: {
    placeholder: string;
    width: string;
    position?: 'top' | 'bottom';
  };
  date?: {
    label: string;
    id: string;
    width: string;
  };
  dateRange?: {
    label: string;
    width: string;
  };
  checkboxLabel?: string;
  checkbox?: {
    id: string;
    label: string | string[];
  }[];
  align?: 'left' | 'right';
  children?: React.ReactNode;
  childrenPosition?: 'top' | 'middle' | 'bottom';
  onChange?: () => void;
  setClassName?: (value: string) => void;
  setStudentName?: (value: string) => void;
};

const getKey = (id: string) => {
  switch (id) {
    case 'course':
      return 'courseId';
    case 'courseClassInfo':
    case 'courseInfo':
    case 'studentCourseByProfessor':
      return 'courseInfoId';
    case 'studClass':
      return 'studClass';
    case 'chartRound':
    case 'chartRoundByPatient':
      return 'chartRound';
    case 'studentUserId':
      return 'studentUserId';
    case 'registrationStatus':
      return 'registrationStatus';
    case 'chartType':
      return 'chartType';
    case 'score':
      return 'score';
    default:
      return '';
  }
};

const getLabel = (id: string) => {
  switch (id) {
    case 'course':
    case 'courseClassInfo':
    case 'studentCourseByProfessor':
      return 'courseName';
    case 'courseInfo':
    case 'studClass':
      return 'studClass';
    case 'chartRound':
    case 'chartRoundByPatient':
      return 'registeredChartName';
    case 'studentUserId':
      return 'studentName/loginId';
    default:
      return '';
  }
};

const getChildren = (id: string) => {
  switch (id) {
    case 'course':
      return ['courseInfo', 'studClass', 'chartRound', 'studentUserId'];
    case 'courseClassInfo':
    case 'studentCourseByProfessor':
      return ['chartRound', 'chartRoundByPatient'];
    case 'courseInfo':
    case 'studClass':
      return ['chartRound', 'studentUserId'];
    default:
      return [];
  }
};

const fixedListIds = ['registrationStatus', 'chartType', 'score'];

const getList = (id: string) => {
  switch (id) {
    case 'registrationStatus':
      return [
        { label: '재학', value: '재학' },
        { label: '휴학', value: '휴학' },
      ];
    case 'chartType':
      return ChartInfo.map((chartType) => ({
        label: chartType.name,
        value: chartType.type,
      }));
    case 'score':
      return [
        { label: '매우 우수', value: 'VERY_HIGH' },
        { label: '우수', value: 'HIGH' },
        { label: '보통', value: 'NORMAL' },
        { label: '낮음', value: 'LOW' },
        { label: '매우 낮음', value: 'VERY_LOW' },
      ];
    default:
      return [];
  }
};

const SearchFilter = ({
  isCollective = false,
  isInnerItem = false,
  dropdown,
  input,
  date,
  dateRange,
  checkboxLabel,
  checkbox,
  align = 'left',
  children,
  childrenPosition = 'top',
  onChange,
  setClassName,
  setStudentName,
}: TProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const { id } = useParams();

  const today = new Date();
  const [startDate, setStartDate] = useState<Date | null>(
    query.get(date?.id || 'startDate')
      ? new Date(query.get(date?.id || 'startDate') || '')
      : null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    query.get('endDate') ? new Date(query.get('endDate') || '') : null
  );

  const defaultFilter: Record<string, string | string[] | boolean | undefined> =
    {};

  if (dropdown) {
    dropdown.forEach((item) => {
      const key = item.id;
      defaultFilter[key] = query.get(getKey(key)) || undefined;
    });
  }
  if (input) {
    defaultFilter.searchWord = query.get(SearchFilterField.searchWord) || '';
  }
  if (checkbox) {
    checkbox.forEach((item) => {
      const key = item.id;
      defaultFilter[key] = Array.isArray(item.label)
        ? query.getAll(key) || []
        : query.get(key) === 'true';
    });
  }

  const [selectList, setSelectList] = useState<{
    [key: string]: TDropdownItem[];
  }>();

  const enableCondition: { [key: string]: boolean } = {
    course: !query.get('courseId') || !selectList?.course,
    courseClassInfo: !query.get('courseInfoId') || !selectList?.courseClassInfo,
    courseInfo:
      !!query.get('courseId') &&
      (!query.get('courseInfoId') || !selectList?.courseInfo),
    studClass:
      !!query.get('courseId') &&
      (!query.get('studClass') || !selectList?.studClass),
    chartRound:
      !!query.get('courseInfoId') &&
      (!query.get('chartRound') || !selectList?.chartRound),
    chartRoundByPatient: !!id && !!query.get('courseInfoId'),
    studentUserId:
      !!query.get('courseInfoId') &&
      !!query.get('chartRound') &&
      (!query.get('studentUserId') || !selectList?.studentUserId),
    studentCourseByProfessor: !!id,
  };

  const fetchList = (id: string, list: TDropdownItem[], useAll: boolean) => {
    setSelectList((prev) => ({
      ...prev,
      [id]: list,
    }));
    setValue(
      id as SearchFilterField,
      query.get(getKey(id))
        ? list.find((data) => data.value == query.get(getKey(id)))?.value || ''
        : !useAll && list.length > 0
        ? list[0]?.value?.toString() || ''
        : ''
    );
    const label = query.get(getKey(id))
      ? list.find((data) => data.value == query.get(getKey(id)))?.label || ''
      : list[0]?.label || '';
    if (setClassName && id === 'courseInfo') {
      setClassName(label);
    }
    if (setStudentName && id === 'studentUserId') {
      setStudentName(label?.split('(')[0]);
    }
    if (!useAll && list.length > 0 && !query.get(getKey(id))) {
      query.set(getKey(id), list[0]?.value?.toString());
      navigate({
        search: query.toString(),
      });
    }
  };

  dropdown?.forEach((item) => {
    if (!fixedListIds.includes(item.id)) {
      const queryKey = [
        `get${item.id}List`,
        ['courseInfo', 'studClass'].includes(item.id)
          ? query.get('courseId')
          : null,
        ['chartRound', 'studentUserId'].includes(item.id)
          ? query.get('courseInfoId')
          : null,
        item.id === 'chartRoundByPatient'
          ? [id, query.get('courseInfoId')]
          : null,
        ['studentUserId'].includes(item.id) ? query.get('chartRound') : null,
      ].filter(Boolean);

      useQuery(
        queryKey,
        async () => {
          switch (item.id) {
            case 'course':
              return await getCourseList();
            case 'courseClassInfo':
              return await getCourseInfoList();
            case 'courseInfo':
            case 'studClass':
              return await getStudClassList(query.get('courseId') ?? '');
            case 'chartRound':
              return await getChartRoundList(query.get('courseInfoId') ?? '');
            case 'chartRoundByPatient':
              return await getChartRoundListByPatient({
                patientId: Number(id),
                courseInfoId: Number(query.get('courseInfoId')),
              });
            case 'studentUserId':
              return await getStudentUserList(
                query.get('courseInfoId') ?? '',
                query.get('chartRound') ?? ''
              );
            case 'studentCourseByProfessor':
              return await getStudentCourseListByProfessor(Number(id));
            default:
              return false;
          }
        },
        {
          enabled: enableCondition[item.id],
          onSuccess: ({
            data,
          }:
            | TAxiosResponse<TGetCourseListResponse>
            | TAxiosResponse<TGetCourseInfoListResponse>
            | TAxiosResponse<TGetStudClassListResponse>
            | TAxiosResponse<TGetChartRoundResponse>
            | TAxiosResponse<TGetStudentUserListResponse>) => {
            const list =
              data.map((listItem: { [key: string]: string | number }) => {
                const labelData = getLabel(item.id)?.split('/');
                const listLabel =
                  listItem[labelData[0]] +
                  (labelData.length > 1 ? ` (${listItem[labelData[1]]})` : '');

                return {
                  label: listLabel?.toString(),
                  value: listItem[getKey(item.id)],
                };
              }) || [];
            fetchList(item.id, list, item.useAll ?? false);
          },
        }
      );
    }
  });

  const handleDropdown = (
    name: string,
    selectedItem: {
      label: string;
      value: string | number | null;
    }
  ) => {
    const value = selectedItem.value;
    const newParams = {
      [getKey(name)]: value as string,
    };

    setSelectList((prev) => {
      const updatedSelectList = { ...prev };
      getChildren(name).forEach((key) => {
        query.delete(getKey(key));
        delete updatedSelectList[key];
      });
      return updatedSelectList;
    });

    Object.keys(newParams).forEach((key) => {
      if (!value) {
        query.delete(key);
      } else {
        query.set(key, value.toString());
      }
    });
    navigate({
      search: query.toString(),
    });
    onChange && onChange();
    if (setClassName && name === 'courseInfo') {
      setClassName(selectedItem.label);
    }
    if (setStudentName && name === 'studentUserId') {
      setStudentName(selectedItem.label?.split('(')[0]);
    }
  };

  const methods = useForm<TSearchFilter>({
    defaultValues: defaultFilter,
  });
  const { handleSubmit, control, reset, setValue } = methods;

  const searchWord = useWatch({
    control,
    name: SearchFilterField.searchWord,
  });

  const handleSearchWord = () => {
    if (!searchWord) {
      query.delete(SearchFilterField.searchWord);
    } else {
      query.set(SearchFilterField.searchWord, searchWord);
    }
    if (!isCollective) {
      navigate({
        search: query.toString(),
      });
      onChange && onChange();
    }
  };

  const checkboxIds =
    checkbox?.map((checkbox) => checkbox.id as keyof TSearchFilter) || [];

  const checkboxValues = useWatch({
    control,
    name: [...checkboxIds] as const,
  });

  useEffect(() => {
    if (!isCollective) {
      let changed = false;
      checkboxIds.forEach((checkboxId, index) => {
        if (checkboxValues[index]) {
          query.set(checkboxId, 'true');
          changed = true;
        } else if (query.has(checkboxId)) {
          query.delete(checkboxId);
          changed = true;
        }
      });
      if (changed) {
        navigate({
          search: query.toString(),
        });
        onChange && onChange();
      }
    }
  }, [checkboxValues]);

  useEffect(() => {
    if (!Array.from(query.keys()).length) {
      reset(defaultFilter);
    }
    dropdown
      ?.filter((item) => fixedListIds.includes(item.id))
      ?.forEach((item) => {
        fetchList(item.id, getList(item.id), item.useAll ?? false);
      });
  }, [location.search]);

  const handleReset = () => {
    setStartDate(null);
    setEndDate(null);
    Array.from(query.keys()).forEach((key) => {
      query.delete(key);
    });
    navigate({
      search: query.toString(),
    });
    onChange && onChange();
  };

  const handleSearch = () => {
    if (input) handleSearchWord();
    if (date || dateRange) {
      if (startDate) {
        query.set(
          date && date?.id ? date.id : 'startDate',
          format(startDate, 'yyyy.MM.dd')
        );
      } else {
        query.delete('startDate');
      }
      if (dateRange) {
        if (endDate) {
          query.set('endDate', format(endDate, 'yyyy.MM.dd'));
        } else {
          query.delete('endDate');
        }
      }
    }
    if (checkbox) {
      checkboxIds.forEach((checkboxId, index) => {
        if (checkboxValues[index]) {
          if (query.has(checkboxId)) query.delete(checkboxId);
          if (Array.isArray(checkboxValues[index])) {
            (checkboxValues[index] as string[]).map((value) => {
              query.append(checkboxId, value);
            });
          } else {
            query.set(checkboxId, checkboxValues[index]);
          }
        } else {
          query.delete(checkboxId);
        }
      });
    }
    navigate({
      search: query.toString(),
    });
    onChange && onChange();
  };

  return (
    <FormProvider {...methods}>
      {isCollective && <h1 className="main_title mt40">필터</h1>}
      <form
        onSubmit={handleSubmit(() => {
          return;
        })}
      >
        {(dropdown || input) && (
          <div
            className={`flex ${!isCollective ? 'gap16' : 'justify_between'} ${
              checkbox
                ? 'mt24 mb16'
                : isCollective
                ? 'mt24'
                : !isInnerItem
                ? 'my24'
                : ''
            }${
              (!isCollective && !dropdown && input) || align === 'right'
                ? '  justify_end'
                : ''
            }`}
          >
            {isCollective && input && (
              <LabelInput
                labelText="검색 입력"
                name={SearchFilterField.searchWord}
                wrapperStyle={{ width: input.width }}
                placeholderText={input.placeholder}
                onBtnClick={handleSearch}
              />
            )}
            {dropdown?.map((item, index) => {
              const component = (
                <FormDropdown
                  key={index}
                  name={item.id}
                  selectList={selectList ? selectList[item.id] || [] : []}
                  {...(!item.useAll &&
                    selectList &&
                    selectList[item.id] && {
                      defaultValue: selectList[item.id][0],
                    })}
                  btnStyle={{
                    width: item.width,
                    ...(isCollective && {
                      height: '48px',
                      background: STYLES_VARIABLES.WHITE,
                    }),
                  }}
                  dropdownStyle={{ width: item.width }}
                  placeholder={item.placeholder}
                  useAll={item.useAll}
                  onClick={handleDropdown}
                />
              );
              return !item.label ? (
                component
              ) : (
                <div key={index}>
                  <p className="mb8 lh22 fs14 fw700 block">{item.label}</p>
                  {component}
                </div>
              );
            })}
            {(date || dateRange) && (
              <div>
                <p className="mb8 lh22 fs14 fw700 block">
                  {date?.label || dateRange?.label}
                </p>
                <div className="flex items_center">
                  <CustomDatePicker
                    date={startDate}
                    setDate={setStartDate}
                    maxDate={today}
                    calendarStyle={{
                      padding: '12px 16px',
                      width: date?.width || dateRange?.width,
                      height: '48px',
                      background: STYLES_VARIABLES.WHITE,
                    }}
                    placeholder={format(today, 'yyyy.MM.dd')}
                  />
                  {dateRange && (
                    <>
                      <span className="mx8">~</span>
                      <CustomDatePicker
                        date={endDate}
                        setDate={setEndDate}
                        minDate={startDate}
                        maxDate={today}
                        calendarStyle={{
                          padding: '12px 16px',
                          width: dateRange.width,
                          height: '48px',
                          background: STYLES_VARIABLES.WHITE,
                        }}
                        placeholder={format(today, 'yyyy.MM.dd')}
                      />
                    </>
                  )}
                </div>
              </div>
            )}
            {!isCollective && input && input.position !== 'bottom' && (
              <>
                <SearchInputWithButton
                  input={input}
                  handleSearchWord={handleSearchWord}
                />
              </>
            )}
            {childrenPosition === 'top' && children}
          </div>
        )}

        {childrenPosition === 'middle' && children}

        {checkbox && (
          <div
            className={
              isCollective
                ? 'mt32'
                : `flex items_center justify_between${
                    !isInnerItem ? ' mb16' : ''
                  }${!dropdown && !input && !isInnerItem ? ' mt4' : ''}`
            }
          >
            {checkboxLabel && (
              <p className="mb14 lh22 fs14 fw700">{checkboxLabel}</p>
            )}
            {checkbox.map((item, index) => {
              if (Array.isArray(item.label)) {
                return item.label.map((label, index) => (
                  <FormCheckbox
                    key={index}
                    name={item.id}
                    text={label}
                    value={label}
                    labelStyle={{
                      marginLeft: index > 0 ? '24px' : '40px',
                      paddingLeft: 0,
                      color: STYLES_VARIABLES.GRAY_09,
                    }}
                    multiple={true}
                  />
                ));
              } else {
                return (
                  <FormCheckbox
                    key={index}
                    name={item.id}
                    text={item.label}
                    value={item.label}
                    checked={checkboxValues[index]}
                    labelStyle={{
                      marginLeft: index > 0 ? '24px' : 0,
                      paddingLeft: 0,
                      color: STYLES_VARIABLES.GRAY_07,
                    }}
                  />
                );
              }
            })}
            {input && input.position === 'bottom' && (
              <div className="flex gap16">
                <SearchInputWithButton
                  input={input}
                  handleSearchWord={handleSearchWord}
                />
              </div>
            )}
            {((!dropdown && !input) || childrenPosition === 'bottom') &&
              children}
          </div>
        )}

        {isCollective && (
          <div className="flex gap24 py24 justify_end">
            <button
              type="button"
              className="flex items_center"
              onClick={handleReset}
            >
              <ResetIcon fill={STYLES_VARIABLES.GRAY_07} />
              <span className="ml5 fs14 font_gray7">초기화</span>
            </button>
            <SingleBtn
              btnText="검색"
              isBtnActive={true}
              onClick={handleSearch}
            />
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export default SearchFilter;
